import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ViewChildren,
  QueryList,
  Renderer2,
  OnInit,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Colors } from '../../../shared/enums/shared.enums';
import { Caasnewsticker } from '../../../shared/interfaces/caas/caas.interface';
import { newstickerItem } from '../../../shared/interfaces/distractor.interface';
import { LinkComponent } from '../../atoms/link/link.component';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';

@Component({
  selector: 'hdi-newsticker',
  templateUrl: './newsticker.component.html',
  styleUrls: ['./newsticker.component.scss'],
  standalone: true,
  imports: [CommonModule, LinkComponent, CapitalizePipe],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class NewstickerComponent implements AfterViewInit, OnInit, OnChanges {
  @Input() caasData?: Caasnewsticker;
  @Input() items: newstickerItem[] = [];
  @Input() bgColor: Colors = Colors.WHITE;

  @ViewChildren('newstickerItems') newstickerItems!: QueryList<ElementRef>;
  @ViewChild('newstickerContainer') newstickerContainer!: ElementRef;

  inline: LinkType = LinkType.INLINE;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private caasRoutesService: CaasRoutesService,
    private router: Router
  ) {}

  currentTime!: Date;
  startTime!: Date;
  endTime!: Date;
  isWithinTimeframe!: boolean;

  ngOnInit(): void {
    this.updateDynamicGradient();
    this.currentTime = new Date();
    this.startTime = new Date('2024-06-24T14:00:00');
    this.endTime = new Date('2025-06-24T15:00:00');
    this.checkTimeframe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bgColor']) {
      this.updateDynamicGradient();
    }
    if (changes['caasData']) {
      this.setCaasData();
    }
  }
  setCaasData(): void {
    if (this.caasData) {
      const linkText = this.caasData.formData?.lt_linktext?.value || '';
      const header = this.caasData.formData?.st_header?.value || '';
      const linkUrl =
        this.caasData.formData?.st_link?.value?.formData?.lt_link_external
          ?.value ||
        (this.caasRoutesService.getSeoRouteByIdentifier(
          this.caasData.formData?.st_link?.value?.formData?.lt_link_internal
            ?.value?.identifier ?? '',
          this.router.config
        )?.path ??
          '');
      const linkFragment =
        this.caasData.formData?.st_link?.value?.formData?.st_anchor_name?.value?.trim();

      const hrefType =
        this.caasData.formData?.st_link?.value?.formData?.lt_link_type?.value
          ?.identifier === 'external'
          ? HrefType.EXTERNAL
          : HrefType.ANGULAR_ROUTE;

      const linkTitle =
        this.caasData.formData?.st_link?.value?.formData?.lt_link_title
          ?.value || '';
      const linkRel =
        this.caasData.formData?.st_link?.value?.formData?.lt_link_follow?.value
          ?.identifier ?? '';
      const linkTarget =
        this.caasData.formData?.st_link?.value?.formData?.lt_link_type?.value
          ?.identifier === 'external'
          ? '_blank'
          : '_self';

      this.items = [
        {
          link: {
            url: linkUrl,
            text: linkText,
            title: linkTitle,
            rel: linkRel,
            target: linkTarget,
            type: hrefType,
            fragment: linkFragment,
          },
          content: header,
        },
      ];
    }
  }
  checkTimeframe(): void {
    if (
      this.currentTime >= this.startTime &&
      this.currentTime <= this.endTime
    ) {
      this.isWithinTimeframe = true;
    } else {
      this.isWithinTimeframe = false;
    }
  }

  ngAfterViewInit(): void {
    this.cloneItems();
  }
  private clonedItems: boolean = false;

  cloneItems(): void {
    if (!this.newstickerItems || this.newstickerItems.length === 0) {
      console.warn('No newsticker items to clone.');
      return;
    }
    if (this.clonedItems) {
      return;
    } // Perform cloning logic
    this.clonedItems = true;
    const containerWidth: number =
      this.newstickerContainer.nativeElement.offsetWidth;
    let itemsWidth: number = this.calculateItemsWidth();

    const clonesNeeded: number = Math.ceil(containerWidth / itemsWidth) * 2;

    for (let i = 0; i < clonesNeeded; i++) {
      this.newstickerItems.forEach((itemRef) => {
        const item: HTMLElement = itemRef.nativeElement;
        const clone: Node = item.cloneNode(true);
        const parentElement =
          this.newstickerItems.first.nativeElement.parentElement;
        if (parentElement) {
          parentElement.appendChild(clone);
        }
      });
    }

    itemsWidth = this.calculateItemsWidth();
  }

  calculateItemsWidth(): number {
    if (!this.newstickerItems || this.newstickerItems.length === 0) {
      return 0;
    }

    return this.newstickerItems.reduce(
      (totalWidth, item) => totalWidth + (item.nativeElement.offsetWidth || 0),
      0
    );
  }

  updateDynamicGradient(): void {
    // Default the color to white if it is invalid
    const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(this.bgColor);
    if (!isValidHex || this.bgColor.toUpperCase() === '#FFFFFF') {
      this.bgColor = Colors.WHITE;
    }

    const styleContent = `
      .newsticker-container:before {
        background: linear-gradient(90deg, ${this.bgColor} 5%, transparent 55%, transparent 100%);
      }
      .newsticker-container:after {
        background: linear-gradient(270deg, ${this.bgColor} 5%, transparent 55%, transparent 100%);
      }
    `;

    // Remove any existing style tag to avoid duplication
    const existingStyleTag = this.el.nativeElement.querySelector(
      'style.dynamic-gradient'
    );
    if (existingStyleTag) {
      this.renderer.removeChild(this.el.nativeElement, existingStyleTag);
    }

    // Create a new style tag with the updated gradient
    const styleTag = this.renderer.createElement('style');
    this.renderer.setProperty(styleTag, 'type', 'text/css');
    this.renderer.addClass(styleTag, 'dynamic-gradient'); // Add a class for identification
    this.renderer.setProperty(styleTag, 'innerHTML', styleContent);

    // Append the style tag to the component's root element
    this.renderer.appendChild(this.el.nativeElement, styleTag);
  }
}
